import type { ComponentProps, ReactNode } from 'react'
import { memo, useCallback, useEffect } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import LayoutContainer from 'components/Common/LayoutContainer'
import Image from 'components/Common/Image'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import { _TextBlockIcon } from 'LuxKit/TextBlocks/_TextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { useInView } from 'react-intersection-observer'

import useExperiencesHomepagePersonalisationData from 'components/Experiences/hooks/useExperiencesHomepagePersonalisationData'

import { useExperiencesHomepageUpliftTracking } from './experiencesHomepageUpliftAnalytics'

const THUMBNAIL_SIZE = 44

const messages = defineMessages({
  heading: {
    id: '8bhvPOl-u0a-L9WBDWmC',
    defaultMessage: 'Things to do in {placeName}',
    description: 'Heading on the Experiences homepage personalisation banner for an upcoming hotel stay destination',
  },
  description: {
    id: 'Up5p3pB3DH7kTqC08o9G',
    defaultMessage: 'Browse {experienceCount} experiences near your stay at <hotelName>{propertyName}</hotelName> in <month>{stayMonth}</month>.',
    description: 'Body copy on the Experiences homepage personalisation banner showing experience count, hotel name, and stay month',
  },
  descriptionWithLink: {
    id: 'OVvgXPvW4ssiZZExU1cE',
    defaultMessage: 'Browse {experienceCount} experiences near your stay at <hotelName>{propertyName}</hotelName> in <month>{stayMonth}</month>. <link>{exploreNowLabel}</link>',
    description: 'Mobile body copy on the Experiences homepage personalisation banner with an inline explore link at the end of the sentence',
  },
  exploreNowCta: {
    id: 'avJIaXykgdP2H7TRGCuP',
    defaultMessage: 'Explore now',
    description: 'Call to action on the Experiences homepage personalisation banner linking to experience search results',
  },
})

const ThumbnailWrapper = styled.div`
  width: ${rem(THUMBNAIL_SIZE)};
  height: ${rem(THUMBNAIL_SIZE)};
  flex-shrink: 0;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.S};
`

const StyledMessageBanner = styled(MessageBanner)`
  ${_TextBlockIcon} {
    height: ${rem(THUMBNAIL_SIZE)};
    align-self: start;
  }

  ${mediaQueryUp.tablet} {
    .kind-secondary {
      --button-color: ${props => props.theme.palette.neutral.surface.normalContrast};
      --button-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      --button-hover-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
      --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      --button-hover-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      --button-active-color: ${props => props.theme.palette.neutral.surface.lightContrast};
      --button-active-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      --button-active-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      --button-focus-color: ${props => props.theme.palette.neutral.surface.normalContrast};
      --button-focus-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      --button-focus-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    }
  }
`

type MessageBannerActionProps = NonNullable<ComponentProps<typeof MessageBanner>['primaryActionButtonProps']>

function ExperiencesHomepagePersonalisation() {
  const intl = useIntl()
  const data = useExperiencesHomepagePersonalisationData()
  const isTabletUp = useScreenSizeUp('tablet')
  const [trackingRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })
  const trackInteraction = useExperiencesHomepageUpliftTracking('booking_personalisation')

  const renderBoldBodyText = useCallback((chunks: ReactNode) => (
    <BodyText as="span" variant="medium" weight="bold">{chunks}</BodyText>
  ), [])

  const trackExploreNowClick = useCallback(() => {
    trackInteraction('booking_personalisation', 'explore_now_clicked')
  }, [trackInteraction])

  useEffect(() => {
    if (inView && data) {
      trackInteraction('booking_personalisation', 'impression')
    }
  }, [data, inView, trackInteraction])

  if (!data) {
    return null
  }

  const exploreNowLabel = intl.formatMessage(messages.exploreNowCta)
  const descriptionValues = {
    experienceCount: data.experienceCount,
    propertyName: data.propertyName,
    stayMonth: data.stayMonth,
    hotelName: renderBoldBodyText,
    month: renderBoldBodyText,
  }

  return (
    <LayoutContainer ref={trackingRef}>
      <StyledMessageBanner
        kind="informational"
        density={isTabletUp ? 'comfortable' : 'compact'}
        heading={(
          <Heading variant="heading5" as="span">
            {intl.formatMessage(messages.heading, { placeName: data.placeName })}
          </Heading>
        )}
        icon={(
          <ThumbnailWrapper>
            <Image
              id={data.imageId}
              width={THUMBNAIL_SIZE}
              height={THUMBNAIL_SIZE}
              fit="center"
              objectFit="cover"
              alt=""
            />
          </ThumbnailWrapper>
        )}
        primaryActionButtonProps={isTabletUp ? {
          to: data.searchUrl,
          children: exploreNowLabel,
          onClick: trackExploreNowClick,
        } as MessageBannerActionProps : undefined}
      >
        <CSSBreakpoint max="mobile">
          <FormattedMessage
            {...messages.descriptionWithLink}
            values={{
              ...descriptionValues,
              exploreNowLabel,
              link: (chunks: ReactNode) => (
                <TextLink key="explore-now" to={data.searchUrl} onClick={trackExploreNowClick}>{chunks}</TextLink>
              ),
            }}
          />
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <FormattedMessage
            {...messages.description}
            values={descriptionValues}
          />
        </CSSBreakpoint>
      </StyledMessageBanner>
    </LayoutContainer>
  )
}

export default memo(ExperiencesHomepagePersonalisation)
